import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { PlaylistVideo } from '@/store/playlist/types';
import { cn } from '@/lib/utils';
import { Play, Check, Circle } from 'lucide-react';
import { formatTime } from '@/utils/formatTime';

interface EpisodeItemProps {
  video: PlaylistVideo;
  index: number;
  isPlaying: boolean;
  isWatched: boolean;
  onSelect: () => void;
  theme: 'purple' | 'blue' | 'dark' | 'white';
}

const themeColors = {
  purple: 'border-[#7A5CFF] bg-[#7A5CFF]/5',
  blue: 'border-[#2D6CF6] bg-[#2D6CF6]/5',
  dark: 'border-white bg-white/5',
  white: 'border-[#7A5CFF] bg-[#7A5CFF]/5',
};

const themeBadgeColors = {
  purple: 'bg-[#7A5CFF]',
  blue: 'bg-[#2D6CF6]',
  dark: 'bg-white',
  white: 'bg-[#7A5CFF]',
};

export const EpisodeItem: React.FC<EpisodeItemProps> = ({
  video,
  index,
  isPlaying,
  isWatched,
  onSelect,
  theme
}) => {
  useLanguage();
  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full p-3 rounded-lg transition-all duration-200",
        "flex items-start gap-3 text-left",
        "hover:bg-muted/50",
        isPlaying && themeColors[theme],
        isPlaying && "border-2"
      )}
    >
      {/* Thumbnail */}
      {video.customThumbnail && (
        <div className="relative w-20 h-12 rounded-md overflow-hidden flex-shrink-0 bg-muted">
          <img 
            src={video.customThumbnail} 
            alt={video.customName || translateInterfaceText("Episode {value0}", { value0: (index + 1) })}
            className="w-full h-full object-cover"
          />
          {isPlaying && (
            <div className={cn(
              "absolute inset-0 flex items-center justify-center",
              themeBadgeColors[theme],
              "bg-opacity-80"
            )}>
              <Play className="w-4 h-4 text-white fill-white" />
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <span className="font-semibold text-sm">
            {index + 1}. {video.customName || translateInterfaceText("Episódio {value0}", { value0: (index + 1) })}
          </span>
        </div>
        
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          {isWatched ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : isPlaying ? (
            <Play className="w-3 h-3" />
          ) : (
            <Circle className="w-3 h-3" />
          )}
          <span>{isWatched ? translateInterfaceText("Assistido") : isPlaying ? translateInterfaceText("Reproduzindo") : translateInterfaceText("Não assistido")}</span>
        </div>
      </div>
    </button>
  );
};
